import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, User as UserIcon, ArrowRight, Copy, ExternalLink } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AUTH } from "@/constants/testIds/app";
import Footer from "@/components/Footer";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const { setUser, refresh } = useAuth();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot password
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotResult, setForgotResult] = useState(null); // { message, reset_url }
  const [forgotError, setForgotError] = useState("");

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const formatError = (detail) => {
    if (detail == null) return "Qualcosa è andato storto. Riprova.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Inserisci email e password");
      return;
    }
    setLoading(true);
    try {
      const url = mode === "register" ? "/auth/register" : "/auth/login";
      const referralCode = (() => {
        try { return localStorage.getItem("recenup-referral") || null; } catch { return null; }
      })();
      const body =
        mode === "register"
          ? { email, password, first_name: firstName || null, last_name: lastName || null, referral_code: referralCode }
          : { email, password };
      const { data } = await api.post(url, body);
      if (mode === "register") {
        try { localStorage.removeItem("recenup-referral"); localStorage.removeItem("recenup-referral-at"); } catch {}
      }
      setUser(data);
      // Fetch full profile (with is_admin flag) before navigating
      await refresh().catch(() => {});
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError("");
  };

  const openForgot = () => {
    setForgotEmail(email);
    setForgotResult(null);
    setForgotError("");
    setForgotOpen(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotError("");
    if (!forgotEmail) {
      setForgotError("Inserisci la tua email");
      return;
    }
    setForgotLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotResult(data);
    } catch (err) {
      setForgotError(err.response?.data?.detail || "Impossibile inviare la richiesta");
    } finally {
      setForgotLoading(false);
    }
  };

  const copyResetUrl = async () => {
    if (!forgotResult?.reset_url) return;
    try {
      await navigator.clipboard.writeText(forgotResult.reset_url);
    } catch (err) {
      // Clipboard API can throw on unsupported browsers or denied permission — non-fatal
      console.debug("clipboard write skipped:", err?.name || err);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[var(--bg-1)]">
      {/* Left pane - image */}
      <div className="relative hidden md:block overflow-hidden">
        <img
          src="https://images.pexels.com/photos/36702003/pexels-photo-36702003.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940"
          alt="Bottega italiana"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="font-serif text-3xl tracking-tight leading-none">
              Recen<span className="italic text-[#00F2F4]">Up</span>
            </div>
          </div>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight">
              Il flusso delle tue<br />
              <span className="italic">recensioni,</span> in un tap.
            </h1>
            <p className="mt-6 max-w-md text-sm text-white/80 leading-relaxed">
              Dashboard moderna per proprietari di attività: seleziona un periodo, vedi la distribuzione delle stelle e monitora l'andamento delle recensioni Google in tempo reale.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right pane - auth */}
      <div className="flex items-center justify-center p-8 md:p-16">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-md"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00F2F4] font-sans font-semibold">
            {mode === "register" ? "Inizia" : "Bentornato"}
          </div>
          <h2 className="mt-3 font-serif text-5xl text-[var(--fg-1)] tracking-tight leading-[1.05]">
            {mode === "register" ? (
              <>Crea il tuo <span className="italic">flusso</span></>
            ) : (
              <>Accedi al tuo <span className="italic">flusso</span></>
            )}
          </h2>
          <p className="mt-4 text-sm text-[var(--fg-3)] leading-relaxed">
            {mode === "register"
              ? "Registrati con email o continua con Google. Un dataset demo di recensioni è pronto per iniziare subito."
              : "Accedi con la tua email o continua con Google per gestire le tue attività."}
          </p>

          {/* Google */}
          <button
            data-testid={AUTH.loginGoogleBtn}
            onClick={handleGoogleLogin}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] rounded-full py-3.5 px-6 font-sans text-sm font-medium focus:ring-2 focus:ring-[#00F2F4] focus:outline-none"
            style={{ transition: "background-color 0.2s ease" }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.6 2.1 30.1 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.8 6c1.9-5.6 7.1-9.8 13.7-9.8z"/>
              <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.2 7.1-17.6z"/>
              <path fill="#FBBC05" d="M10.3 28.7c-.5-1.5-.8-3.1-.8-4.7s.3-3.2.8-4.7l-7.8-6C.9 16.6 0 20.2 0 24s.9 7.4 2.5 10.7l7.8-6z"/>
              <path fill="#34A853" d="M24 48c6.5 0 12-2.1 16-5.8l-7.6-5.9c-2.1 1.4-4.8 2.2-8.4 2.2-6.5 0-12-4.4-13.9-10.3l-7.8 6C6.4 42.6 14.6 48 24 48z"/>
            </svg>
            Continua con Google
          </button>

          <div className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[var(--fg-4)]">
            <div className="h-px flex-1 bg-[var(--border-c)]" />
            oppure con email
            <div className="h-px flex-1 bg-[var(--border-c)]" />
          </div>

          {/* Email/password form */}
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <AnimatePresence initial={false}>
              {mode === "register" && (
                <motion.div
                  key="names"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  className="grid grid-cols-2 gap-3 overflow-hidden"
                >
                  <div>
                    <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                      Nome
                    </Label>
                    <div className="mt-2 relative">
                      <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
                      <Input
                        data-testid={AUTH.firstNameInput}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Marco"
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                      Cognome
                    </Label>
                    <div className="mt-2 relative">
                      <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
                      <Input
                        data-testid={AUTH.lastNameInput}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Rossi"
                        className="pl-9"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                Email
              </Label>
              <div className="mt-2 relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
                <Input
                  data-testid={AUTH.emailInput}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@esempio.it"
                  className="pl-9"
                />
              </div>
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Password
                </Label>
                {mode === "login" && (
                  <button
                    type="button"
                    data-testid={AUTH.forgotLink}
                    onClick={openForgot}
                    className="text-[10px] font-sans font-semibold uppercase tracking-[0.15em] text-[#00F2F4] hover:text-[#00D6D8]"
                    style={{ transition: "color 0.2s ease" }}
                  >
                    Password dimenticata?
                  </button>
                )}
              </div>
              <div className="mt-2 relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
                <Input
                  data-testid={AUTH.passwordInput}
                  type="password"
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "register" ? "Almeno 12 caratteri" : "••••••••"}
                  className="pl-9"
                />
              </div>
              {mode === "register" && (
                <PasswordStrengthMeter password={password} testId="register-password-meter" />
              )}
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={AUTH.errorMessage}
                className="text-xs font-sans text-[#C6423B] bg-[#C6423B]/10 border border-[#C6423B]/20 rounded-lg px-3 py-2"
              >
                {error}
              </motion.div>
            )}

            <button
              type="submit"
              data-testid={AUTH.submitBtn}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] rounded-full py-3.5 px-6 font-sans text-sm font-semibold disabled:opacity-60 focus:ring-2 focus:ring-[#00F2F4]/50 focus:outline-none"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {loading ? "Attendere..." : mode === "register" ? "Crea account" : "Accedi"}
              {!loading && <ArrowRight size={15} />}
            </button>
          </form>

          {/* Switch mode */}
          <div className="mt-6 flex items-center justify-center gap-1 text-xs font-sans text-[var(--fg-3)]">
            {mode === "register" ? (
              <>
                <span>Hai già un account?</span>
                <button
                  data-testid={AUTH.tabLogin}
                  onClick={() => switchMode("login")}
                  className="ml-1 text-[var(--fg-1)] font-semibold hover:text-[#00F2F4]"
                  style={{ transition: "color 0.2s ease" }}
                >
                  Accedi
                </button>
              </>
            ) : (
              <>
                <span>Non hai un account?</span>
                <button
                  data-testid={AUTH.tabRegister}
                  onClick={() => switchMode("register")}
                  className="ml-1 text-[var(--fg-1)] font-semibold hover:text-[#00F2F4]"
                  style={{ transition: "color 0.2s ease" }}
                >
                  Registrati
                </button>
              </>
            )}
          </div>

          <p className="mt-8 text-xs text-[var(--fg-4)] leading-relaxed text-center">
            Continuando accetti i nostri{" "}
            <a href="/terms" className="underline hover:text-[var(--fg-2)]">Termini di servizio</a> e la nostra{" "}
            <a href="/privacy" className="underline hover:text-[var(--fg-2)]">Privacy</a>. Un set demo di recensioni è pronto per esplorare la dashboard.
          </p>
        </motion.div>
      </div>

      <Footer />

      {/* Forgot password dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-[var(--bg-2)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[var(--fg-1)]">
              Recupera password
            </DialogTitle>
          </DialogHeader>

          {!forgotResult ? (
            <form onSubmit={submitForgot} className="space-y-4">
              <p className="text-sm text-[var(--fg-3)]">
                Inserisci l'email del tuo account. Ti invieremo un link per reimpostare la password.
              </p>
              <div>
                <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Email
                </Label>
                <div className="mt-2 relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
                  <Input
                    data-testid={AUTH.forgotEmailInput}
                    type="email"
                    autoComplete="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="tu@esempio.it"
                    className="pl-9"
                    autoFocus
                  />
                </div>
              </div>
              {forgotError && (
                <div className="text-xs font-sans text-[#C6423B] bg-[#C6423B]/10 border border-[#C6423B]/20 rounded-lg px-3 py-2">
                  {typeof forgotError === "string" ? forgotError : "Errore"}
                </div>
              )}
              <button
                type="submit"
                data-testid={AUTH.forgotSubmitBtn}
                disabled={forgotLoading}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] rounded-full py-3 px-6 font-sans text-sm font-semibold disabled:opacity-60"
                style={{ transition: "background-color 0.2s ease" }}
              >
                {forgotLoading ? "Invio..." : "Invia link di reset"}
                {!forgotLoading && <ArrowRight size={15} />}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-[var(--fg-2)] leading-relaxed">
                {forgotResult.message}
              </p>
              {forgotResult.reset_url ? (
                <>
                  <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                    Il tuo link di reset (demo)
                  </div>
                  <div
                    data-testid={AUTH.forgotResetUrl}
                    className="p-3 bg-[var(--bg-3)] border border-[var(--border-c)] rounded-lg text-xs font-mono break-all text-[var(--fg-2)]"
                  >
                    {forgotResult.reset_url}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={copyResetUrl}
                      className="flex-1 inline-flex items-center justify-center gap-2 border border-[var(--border-c)] rounded-full py-2.5 px-4 text-sm font-sans text-[var(--fg-1)] hover:border-[var(--fg-2)]"
                      style={{ transition: "border-color 0.2s ease" }}
                    >
                      <Copy size={14} /> Copia link
                    </button>
                    <a
                      href={forgotResult.reset_url}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] rounded-full py-2.5 px-4 text-sm font-sans font-semibold"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      <ExternalLink size={14} /> Apri
                    </a>
                  </div>
                  <p className="text-[10px] text-[var(--fg-4)] leading-relaxed">
                    In produzione questo link verrebbe inviato per email. Scade tra {forgotResult.expires_in_minutes || 60} minuti.
                  </p>
                </>
              ) : (
                <p className="text-xs text-[var(--fg-4)]">
                  Se l'email è registrata riceverai un link. Controlla la tua casella.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
